import { useState, type ReactElement } from 'react';
import { useTranslation } from 'react-i18next';
import { ArrowDown, ArrowUp } from 'lucide-react';
import { FORM_KEYS, formFields, type FormKey } from '@tailoring-crm/shared';
import { PageHeader } from '../components/layout/page-header.js';
import {
  Button,
  Card,
  CardContent,
  CardHeader,
  CardTitle,
  ListSkeleton,
  useToast,
} from '@tailoring-crm/ui';
import { isApiRequestError } from '../lib/api/errors.js';
import { useAuth } from '../lib/auth/auth-context.js';
import {
  usePresentationSettings,
  useFormLayouts,
  useUpdateShopSettings,
} from '../lib/settings/use-shop-settings.js';

/**
 * What each form asks for, and in what order.
 *
 * SHOP-WIDE, unlike the table columns each person keeps for themselves, and the difference is
 * not arbitrary: a column is how one person likes to read a list, while a form layout decides
 * what the shop RECORDS — and two people recording different fields about the same customer is
 * a data problem, not a preference.
 *
 * MOVE UP AND MOVE DOWN, not drag and drop. This screen is used on a phone as often as on a
 * laptop, and a drag target that has to work under a thumb on a scrolling list is a feature
 * that works in a demo. Two buttons are duller and work everywhere.
 *
 * A REQUIRED FIELD IS SHOWN, CHECKED AND DISABLED rather than left out. Leaving it out would
 * make somebody wonder where the name box went; showing it greyed says plainly that the API
 * would refuse a customer without one, so hiding it would only produce a form nobody can
 * submit.
 */
export function FormLayoutsPage(): ReactElement {
  const { t } = useTranslation();
  const { role } = useAuth();
  const settings = usePresentationSettings();
  const layouts = useFormLayouts();

  const canWrite = role === 'owner';

  if (settings.isPending) return <ListSkeleton rows={5} />;

  return (
    <>
      <PageHeader title={t('formLayouts.title')} description={t('formLayouts.description')} />

      <div className="flex flex-col gap-4">
        {FORM_KEYS.map((form) => (
          <FormLayoutCard
            key={form}
            form={form}
            fields={layouts.find((layout) => layout.form === form)?.fields ?? []}
            allLayouts={layouts}
            canWrite={canWrite}
          />
        ))}
      </div>
    </>
  );
}

function FormLayoutCard({
  form,
  fields,
  allLayouts,
  canWrite,
}: {
  form: FormKey;
  fields: readonly string[];
  allLayouts: readonly { form: FormKey; fields: string[] }[];
  canWrite: boolean;
}): ReactElement {
  const { t } = useTranslation();
  const { toast } = useToast();
  const update = useUpdateShopSettings();
  const [draft, setDraft] = useState<string[] | null>(null);

  const definitions = formFields(form);
  const current = draft ?? [...fields];
  const dirty = draft !== null && draft.join('|') !== fields.join('|');

  const toggle = (key: string): void => {
    setDraft(
      current.includes(key)
        ? current.filter((one) => one !== key)
        : // Inserted in the registry's own order rather than at the end, so switching a field
          // back on puts it where it belongs instead of after everything else.
          definitions
            .filter((one) => one.key === key || current.includes(one.key))
            .map((one) => one.key),
    );
  };

  const move = (key: string, by: number): void => {
    const index = current.indexOf(key);
    const next = index + by;
    if (index < 0 || next < 0 || next >= current.length) return;
    const reordered = [...current];
    const [moved] = reordered.splice(index, 1);
    reordered.splice(next, 0, moved as string);
    setDraft(reordered);
  };

  const save = (): void => {
    void (async () => {
      try {
        // Every form is sent, not only this one: the settings field is a list and the server
        // replaces it wholesale, so sending one card's layout alone would clear the others.
        await update.mutateAsync({
          formLayouts: allLayouts.map((layout) =>
            layout.form === form ? { form, fields: current } : layout,
          ),
        });
        setDraft(null);
        toast({ title: t('formLayouts.saved'), variant: 'success' });
      } catch (caught) {
        toast({
          title: isApiRequestError(caught) ? caught.message : t('error.unexpected'),
          variant: 'destructive',
        });
      }
    })();
  };

  return (
    <Card>
      <CardHeader>
        <CardTitle>{t(`formLayouts.form.${form}`)}</CardTitle>
      </CardHeader>
      <CardContent className="flex flex-col gap-3">
        <ul className="flex flex-col gap-1">
          {/* The chosen fields first, in their order, then the ones that are switched off. */}
          {current.map((key, index) => {
            const definition = definitions.find((one) => one.key === key);
            if (!definition) return null;
            return (
              <li
                key={key}
                className="flex items-center gap-2 rounded-md border border-border px-2 py-1.5"
              >
                <input
                  type="checkbox"
                  checked
                  disabled={definition.required === true || !canWrite}
                  aria-label={t(`forms.${form}.${key}`, definition.defaultLabel)}
                  onChange={() => toggle(key)}
                />
                <span className="min-w-0 flex-1 truncate text-sm">
                  {t(`forms.${form}.${key}`, definition.defaultLabel)}
                </span>
                {canWrite ? (
                  <>
                    <Button
                      variant="ghost"
                      size="sm"
                      aria-label={t('formLayouts.moveUp', {
                        field: t(`forms.${form}.${key}`, definition.defaultLabel),
                      })}
                      disabled={index === 0}
                      onClick={() => move(key, -1)}
                    >
                      <ArrowUp aria-hidden="true" />
                    </Button>
                    <Button
                      variant="ghost"
                      size="sm"
                      aria-label={t('formLayouts.moveDown', {
                        field: t(`forms.${form}.${key}`, definition.defaultLabel),
                      })}
                      disabled={index === current.length - 1}
                      onClick={() => move(key, 1)}
                    >
                      <ArrowDown aria-hidden="true" />
                    </Button>
                  </>
                ) : null}
              </li>
            );
          })}

          {definitions
            .filter((definition) => !current.includes(definition.key))
            .map((definition) => (
              <li
                key={definition.key}
                className="flex items-center gap-2 rounded-md border border-dashed border-border px-2 py-1.5 opacity-70"
              >
                <input
                  type="checkbox"
                  checked={false}
                  disabled={!canWrite}
                  aria-label={t(`forms.${form}.${definition.key}`, definition.defaultLabel)}
                  onChange={() => toggle(definition.key)}
                />
                <span className="min-w-0 flex-1 truncate text-sm">
                  {t(`forms.${form}.${definition.key}`, definition.defaultLabel)}
                </span>
              </li>
            ))}
        </ul>

        {canWrite ? (
          <div className="flex gap-2">
            <Button disabled={!dirty || update.isPending} onClick={save}>
              {t('common.save')}
            </Button>
            <Button variant="ghost" disabled={!dirty} onClick={() => setDraft(null)}>
              {t('common.cancel')}
            </Button>
          </div>
        ) : null}
      </CardContent>
    </Card>
  );
}

export default FormLayoutsPage;
